/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useState, useEffect } from 'preact/hooks';
import { LOADMORE_STYLE_CLASS } from '../PRIVATE_LoadMoreCollection';
import { tableSkeletonBarContainerStyles, tableSkeletonCellStyles } from './themes/Table.css';
import { classNames } from '../utils/UNSAFE_classNames';
import { Skeleton } from '../UNSAFE_Skeleton';

type SkeletonRowProps = {
  /**
   * Total number of columns to span
   */
  colspan: number;
  /**
   * The preact key value
   */
  preactKey: any;
  /**
   * Allows to specify number of skeletons to be rendered on initial load
   **/
  minimumCount?: number;
};

/**
 * Allows to specify the time delay for rendering the component
 **/
const _timerValue = 50;

/**
 * TableSkeletonRow renders 'minimumCount' number of skeletons
 * of the variant specified from its child element - Skeleton's prop after
 * 'timerValue' ms delay
 **/
export function TableSkeletonRow({ colspan, preactKey, minimumCount = 1 }: SkeletonRowProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setIsVisible(true);
    }, _timerValue);
  }, []);

  const cellClasses = classNames([tableSkeletonCellStyles.base]);

  return (
    <tr class={LOADMORE_STYLE_CLASS} key={preactKey}>
      {isVisible ? (
        <td class={cellClasses} colSpan={colspan} role="presentation">
          {[...Array(minimumCount)].map((_element) => (
            <div class={tableSkeletonBarContainerStyles.base}>
              <Skeleton height="4x" />
            </div>
          ))}
        </td>
      ) : null}
    </tr>
  );
}
